import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { assert } from '@ember/debug';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import FormField from './form-field.js';
import { concat } from '@ember/helper';
import { useStyles } from '@frontile/theme';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';
import { d as decorateFieldV2, i as initializeDeferredDecorator, a as decorateMethodV2 } from '../runtime-BPCpkOf1-DV8zmEr2.js';

class FormSelect extends Component {
  static {
    decorateFieldV2(this.prototype, "shouldShowErrorFeedback", [tracked], function () {
      return false;
    });
  }
  #shouldShowErrorFeedback = (initializeDeferredDecorator(this, "shouldShowErrorFeedback"), undefined);
  static {
    decorateFieldV2(this.prototype, "isOpen", [tracked], function () {
      return false;
    });
  }
  #isOpen = (initializeDeferredDecorator(this, "isOpen"), undefined);
  constructor(owner, args) {
    super(owner, args);
    assert('<FormSelect> requires an `@onChange` function', this.args.onChange && typeof this.args.onChange === 'function');
  }
  get showErrorFeedback() {
    if (this.args.hasError === false) {
      return false;
    }
    if ((this.args.showError || this.args.hasSubmitted || this.shouldShowErrorFeedback) && this.args.errors && this.args.errors.length > 0) {
      return true;
    } else {
      return false;
    }
  }
  handleOpen(select, event) {
    this.isOpen = true;
    this.shouldShowErrorFeedback = false;
    if (typeof this.args.onOpen === 'function') {
      this.args.onOpen(select, event);
    }
  }
  static {
    decorateMethodV2(this.prototype, "handleOpen", [action]);
  }
  handleClose(select, event) {
    this.isOpen = false;
    this.shouldShowErrorFeedback = true;
    if (typeof this.args.onClose === 'function') {
      this.args.onClose(select, event);
    }
  }
  static {
    decorateMethodV2(this.prototype, "handleClose", [action]);
  }
  handleFocusIn(select, event) {
    this.shouldShowErrorFeedback = false;
    if (typeof this.args.onFocusIn === 'function') {
      this.args.onFocusIn(select, event);
    }
    // ember-power-select argument
    if (typeof this.args.onFocus === 'function') {
      this.args.onFocus(select, event);
    }
  }
  static {
    decorateMethodV2(this.prototype, "handleFocusIn", [action]);
  }
  handleFocusOut(select, event) {
    if (!this.isOpen) {
      this.shouldShowErrorFeedback = true;
    }
    if (typeof this.args.onFocusOut === 'function') {
      this.args.onFocusOut(select, event);
    }
    // ember-power-select argument
    if (typeof this.args.onBlur === 'function') {
      this.args.onBlur(select, event);
    }
  }
  static {
    decorateMethodV2(this.prototype, "handleFocusOut", [action]);
  }
  handleChange(selection, select, event) {
    this.shouldShowErrorFeedback = true;
    this.args.onChange(selection, select, event);
  }
  static {
    decorateMethodV2(this.prototype, "handleChange", [action]);
  }
  get classes() {
    const {
      formSelect
    } = useStyles();
    const {
      base,
      label,
      select,
      hint,
      feedback
    } = formSelect({
      size: this.args.size
    });
    return {
      base: base({
        class: this.args.containerClass
      }),
      label: label(),
      select: select(),
      hint: hint(),
      feedback: feedback()
    };
  }
  static {
    setComponentTemplate(precompileTemplate("\n    <FormField @size={{@size}} class={{this.classes.base}} as |f|>\n\n      {{#if @label}}\n        <f.Label @for id={{f.id}} @class={{this.classes.label}}>\n          {{@label}}\n        </f.Label>\n      {{/if}}\n\n      {{#if @hint}}\n        <f.Hint @class={{this.classes.hint}}>\n          {{@hint}}\n        </f.Hint>\n      {{/if}}\n\n      {{!-- @glint-nocheck: need to fix powerselect types--}}\n      {{#let (if @isMultiple PowerSelectMultiple PowerSelect) as |Component|}}\n        <Component ...attributes @onChange={{this.handleChange}} @onFocus={{this.handleFocusIn}} @onBlur={{this.handleFocusOut}} @onOpen={{this.handleOpen}} @onClose={{this.handleClose}} @ariaDescribedBy=\"{{if @hint f.hintId}} {{if this.showErrorFeedback (concat \" \" f.feedbackId)}}{{if @ariaDescribedBy (concat \" \" @ariaDescribedBy)}}\" @ariaLabelledBy=\"{{if @label f.id}} {{if @ariaLabelledBy (concat \" \" @ariaLabelledBy)}}\" @ariaInvalid={{if this.showErrorFeedback \"true\"}} @triggerClass=\"{{@triggerClass}} {{if @size (concat \"ember-power-select-trigger-\" @size)}} {{this.classes.select}}\" @highlightOnHover={{@highlightOnHover}} @placeholderComponent={{@placeholderComponent}} @searchMessage={{@searchMessage}} @noMatchesMessage={{@noMatchesMessage}} @matchTriggerWidth={{@matchTriggerWidth}} @options={{@options}} @selected={{@selected}} @closeOnSelect={{@closeOnSelect}} @defaultHighlighted={{@defaultHighlighted}} @searchField={{@searchField}} @searchEnabled={{@searchEnabled}} @tabindex={{@tabindex}} @triggerComponent={{@triggerComponent}} @matcher={{@matcher}} @initiallyOpened={{@initiallyOpened}} @typeAheadOptionMatcher={{@typeAheadOptionMatcher}} @buildSelection={{@buildSelection}} @search={{@search}} @onInput={{@onInput}} @onKeydown={{@onKeydown}} @scrollTo={{@scrollTo}} @registerAPI={{@registerAPI}} @horizontalPosition={{@horizontalPosition}} @destination={{@destination}} @preventScroll={{@preventScroll}} @renderInPlace={{@renderInPlace}} @verticalPosition={{@verticalPosition}} @disabled={{@disabled}} @calculatePosition={{@calculatePosition}} @eventType={{@eventType}} @ariaLabel={{@ariaLabel}} @required={{@required}} @triggerRole={{@triggerRole}} @title={{@title}} @triggerId={{@triggerId}} @allowClear={{@allowClear}} @loadingMessage={{@loadingMessage}} @selectedItemComponent={{@selectedItemComponent}} @dropdownClass={{@dropdownClass}} @beforeOptionsComponent={{@beforeOptionsComponent}} @placeholder={{@placeholder}} @searchPlaceholder={{@searchPlaceholder}} @searchMessageComponent={{@searchMessageComponent}} @optionsComponent={{@optionsComponent}} @extra={{@extra}} @groupComponent={{@groupComponent}} @afterOptionsComponent={{@afterOptionsComponent}} as |option term|>\n          {{yield option term}}\n        </Component>\n      {{/let}}\n\n      {{#if this.showErrorFeedback}}\n        <f.Feedback @class={{this.classes.feedback}} @errors={{@errors}} />\n      {{/if}}\n    </FormField>\n  ", {
      strictMode: true,
      scope: () => ({
        FormField,
        PowerSelectMultiple,
        PowerSelect,
        concat
      })
    }), this);
  }
}

export { FormSelect as default };
//# sourceMappingURL=form-select.js.map
